import { Box, Button, Popper, Tooltip, Typography } from '@mui/material';
import { ReactNode, useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'material-react-toastify';
import { useRecoilValue } from 'recoil';
import { ProductSummary } from '../../interfaces/Product/ProductSummary';
import { sharedColors } from '../../utils/Style';
import { useThemeColor } from '../../hooks/useThemeColor';
import { calculateDiscountRate } from '../../utils/Pricing';
import { isTokenValid } from '../../utils/Auth';
import { setStockAlert } from '../../api/StockAlert';
import { AddToBasketPopper } from './AddToBasketPopper';
import { useLocalizedText } from '../../hooks/useLocalizedText';
import { settingsAtom } from '../../atoms/Settings';
import { ComponentStyle } from '../../interfaces/Settings/SettingsV2/ComponentStyle';

type OverlayChipProps = {
  text: string;
  color: string;
  styleOverride?: ComponentStyle;
};

const OverlayChip = ({ text, color, styleOverride }: OverlayChipProps) => {
  const { secondary } = useThemeColor();

  return (
    <Typography
      sx={{
        backgroundColor: color,
        p: '3px 5px 2px',
        borderRadius: '3px',
        color: secondary,
        fontSize: '11px',
        fontWeight: 500,
        ...styleOverride,
      }}
    >
      {text}
    </Typography>
  );
};

type ProductCardProps = {
  productSummary: ProductSummary;
};

export const ProductCard = ({ productSummary }: ProductCardProps) => {
  const t = useLocalizedText();

  const navigate = useNavigate();

  const { primary, secondary, ternary } = useThemeColor();

  const settings = useRecoilValue(settingsAtom);

  const [hovering, setHovering] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleSetStockAlert = useCallback(() => {
    if (isTokenValid()) {
      setStockAlert({
        productID: productSummary.id,
      })
        .then(() => {
          toast.success(t('product.you_will_be_notified'));
        })
        .catch((error) => toast.error(error.message));
    } else {
      navigate('/giris-yap');
    }
  }, [productSummary]);

  const chips: ReactNode[] = [];
  if (productSummary.specialPrice !== productSummary.price) {
    chips.push(
      <OverlayChip
        text={`%${calculateDiscountRate(
          productSummary.price,
          productSummary.specialPrice,
        ).toString()}`}
        color={primary}
        styleOverride={
          settings.settingsV2?.productCardSettings?.discountChipStyle
        }
      />,
    );
  }
  if (!productSummary.inStock) {
    chips.push(
      <OverlayChip
        text={t('product.chips.soon')}
        color={ternary}
        styleOverride={
          settings.settingsV2?.productCardSettings?.outOfStockChipStyle
        }
      />,
    );
  } else if (productSummary.quantity < 6) {
    chips.push(
      <OverlayChip
        text={t('product.chips.low_stock')}
        color={ternary}
        styleOverride={
          settings.settingsV2?.productCardSettings?.lowStockChipStyle
        }
      />,
    );
  }

  return (
    <Link
      to={`/urun/${productSummary.slug}`}
      style={{ textDecoration: 'none' }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          ...settings.settingsV2?.productCardSettings?.rootStyle,
        }}
      >
        <Box
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          sx={{
            position: 'relative',
            width: '100%',
            paddingBottom: '120%', // 1.2 * width = 120% height
            overflow: 'hidden',
          }}
        >
          <Box
            component='img'
            src={
              hovering && !!productSummary.secondImage
                ? productSummary.secondImage
                : productSummary.imagePath
            }
            sx={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              top: 0,
              left: 0,
              objectFit: 'contain',
              ...settings.settingsV2?.productCardSettings?.imageStyle,
            }}
          />

          {chips.map((chip, i) => (
            <Box
              key={i}
              position='absolute'
              top={5 + Math.floor(i / 2) * 30}
              left={i % 2 === 0 ? 5 : undefined}
              right={i % 2 === 0 ? undefined : 5}
            >
              {chip}
            </Box>
          ))}
        </Box>

        <Tooltip title={productSummary.name}>
          <Typography
            sx={{
              fontSize: '15px',
              fontWeight: 600,
              textAlign: 'center',
              color: sharedColors.gray7,
              transition: 'color 0.3s ease',
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              lineHeight: '1.3em',
              maxHeight: '2.6em',
              minHeight: '2.6em',
              '&:hover': {
                color: primary,
              },
              ...settings.settingsV2?.productCardSettings?.nameStyle,
            }}
          >
            {productSummary.name}
          </Typography>
        </Tooltip>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 0.5,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {productSummary.specialPrice !== productSummary.price && (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '15px',
                color: sharedColors.gray5,
                textDecoration: 'line-through',
                ...settings.settingsV2?.productCardSettings?.oldPriceStyle,
              }}
            >
              {productSummary.priceFormatted}
            </Typography>
          )}

          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '15px',
              color: primary,
              ...settings.settingsV2?.productCardSettings?.priceStyle,
            }}
          >
            {productSummary.specialPrice
              ? productSummary.specialPriceFormatted
              : productSummary.priceFormatted}
          </Typography>
        </Box>

        <Button
          onClick={(ev) => {
            ev.preventDefault();
            ev.stopPropagation();

            if (productSummary.inStock) {
              setAnchorEl(ev.currentTarget);
            } else {
              handleSetStockAlert();
            }
          }}
          sx={{
            fontWeight: 700,
            fontSize: '13px',
            padding: '8px 15px',
            color: secondary,
            backgroundColor: productSummary.inStock ? primary : ternary,
            transition: 'background-color 0.3s ease',
            '&:hover': {
              backgroundColor: ternary,
            },
            ...(productSummary.inStock
              ? settings.settingsV2?.productCardSettings?.addToCartButtonStyle
              : settings.settingsV2?.productCardSettings?.notifyButtonStyle),
          }}
        >
          {productSummary.inStock
            ? t('product.add_to_cart')
            : t('product.notify')}
        </Button>

        <Popper open={!!anchorEl} anchorEl={anchorEl}>
          <AddToBasketPopper
            productSlug={productSummary.slug}
            onClose={() => setAnchorEl(null)}
            atCheckout={false}
          />
        </Popper>
      </Box>
    </Link>
  );
};
